<script setup lang="ts">
import { withDefaults } from 'vue';
import type { TLlmFeedbackRating } from '@/_core/entities/llm-feedback/models/types';
import ThumbsUpIcon from '@/assets/images/svg/thumbs-up.svg?component';
import ThumbsDownIcon from '@/assets/images/svg/thumbs-down.svg?component';

const props = withDefaults(
  defineProps<{
    rating: TLlmFeedbackRating | null;
    isPending?: boolean;
    activeClass?: string;
    inactiveClass?: string;
  }>(),
  {
    activeClass: 'tw:text-main-blue',
    inactiveClass: 'tw:text-secondary hover:tw:text-main-blue',
  }
);

const emit = defineEmits<{
  (e: 'rate', rating: TLlmFeedbackRating): void;
}>();

function handleClick(value: TLlmFeedbackRating) {
  if (props.isPending) return;
  if (props.rating === value) return;
  emit('rate', value);
}
</script>

<template>
  <div class="tw:flex tw:items-center tw:gap-2">
    <span v-if="rating === null" class="tw:text-black"> Was this helpful? </span>
    <div class="tw:relative tw:inline-block group">
      <button
        type="button"
        :disabled="isPending"
        :class="rating === 'like' ? activeClass : inactiveClass"
        class="tw:inline-flex tw:size-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:p-0 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-colors tw:leading-none tw:disabled:cursor-not-allowed tw:disabled:opacity-40 tw:focus:outline-none tw:rounded"
        aria-label="Rate helpful"
        :aria-pressed="rating === 'like'"
        @click="handleClick('like')"
      >
        <ThumbsUpIcon
          aria-hidden="true"
          class="tw:block tw:w-full tw:h-full tw:max-w-[18px] tw:max-h-[18px] tw:shrink-0"
        />
      </button>
      <div
        class="tw:absolute tw:bottom-full tw:left-1/2 tw:-translate-x-1/2 tw:mb-2 tw:px-2 tw:py-1 tw:rounded tw:bg-secondary tw:text-white tw:typography-small tw:whitespace-nowrap tw:opacity-0 group-hover:tw:opacity-100 group-focus-within:tw:opacity-100 tw:pointer-events-none tw:transition-opacity"
      >
        Rate helpful
        <div
          class="tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:border-4 tw:border-transparent tw:border-t-secondary"
        />
      </div>
    </div>
    <div class="tw:relative tw:inline-block group">
      <button
        type="button"
        :disabled="isPending"
        :class="rating === 'dislike' ? activeClass : inactiveClass"
        class="tw:inline-flex tw:size-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:p-0 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-colors tw:leading-none tw:disabled:cursor-not-allowed tw:disabled:opacity-40 tw:focus:outline-none tw:rounded"
        aria-label="Rate not helpful"
        :aria-pressed="rating === 'dislike'"
        @click="handleClick('dislike')"
      >
        <ThumbsDownIcon
          aria-hidden="true"
          class="tw:block tw:w-full tw:h-full tw:max-w-[18px] tw:max-h-[18px] tw:shrink-0"
        />
      </button>
      <div
        class="tw:absolute tw:bottom-full tw:left-1/2 tw:-translate-x-1/2 tw:mb-2 tw:px-2 tw:py-1 tw:rounded tw:bg-secondary tw:text-white tw:typography-small tw:whitespace-nowrap tw:opacity-0 group-hover:tw:opacity-100 group-focus-within:tw:opacity-100 tw:pointer-events-none tw:transition-opacity"
      >
        Rate not helpful
        <div
          class="tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:border-4 tw:border-transparent tw:border-t-secondary"
        />
      </div>
    </div>
  </div>
</template>
